import React, { memo, Suspense, lazy, useMemo } from 'react';
import { css, useTheme } from '@emotion/react';
import type {
  ColourField,
  DLClickEventHandlerProps,
  IconEntryType,
  ImageEntryType,
  LinkEntryType,
  ProductProps,
} from 'tsconfig/types.d';
import { Icon } from '../../Icon/Icon';
import { CmsImage } from '../CmsImage/CmsImage';
import { logout } from 'common-state';
import { dwChat } from '../../../dw-chat/DwChat';
import { logger, inspectorMode } from 'common-ui';
import { Link } from '../../Link/Link';
import { usePathname } from 'next/navigation';
import { useReactWebviewCommunicator } from '../../../hooks/useReactWebviewCommunicator';
import { classNames } from '../../../utils';

const Button = lazy(() => import('../../AddToCart/Button').then(({ CartButton }) => ({ default: CartButton })));

export type CmsLinkProps = {
  contentItem: LinkEntryType;
  category?: string;
  className?: string;
  dlClickEventHandler?: DLClickEventHandlerProps;
  hit?: ProductProps;
};
const UK_COOKIE_TARGET = 'optanon-toggle-display';

const getVariantStyles = (variant: string | undefined, theme: any) => {
  if (!variant) {
    return '';
  }
  const variantMap = {
    'Secondary button': theme.mixins.btnSecondary,
    'Primary button': theme.mixins.btnPrimary,
    'Secondary-inverted button': theme.mixins.btnTertiary,
    'Transparent button': theme.mixins.btnTransparent,
  };

  const style = variantMap[variant as keyof typeof variantMap];
  return style
    ? css`
        && {
          ${style}
        }
      `
    : '';
};

type ButtonVariant = 'secondary' | 'tertiary' | 'transparent' | 'primary';

const getButtonStyle = (variant: string | undefined): ButtonVariant => {
  const styleMap = {
    'Secondary button': 'secondary' as const,
    'Secondary-inverted button': 'tertiary' as const,
    'Transparent button': 'transparent' as const,
  };

  return styleMap[variant as keyof typeof styleMap] || 'primary';
};

const createClickHandler =
  (targeting: string | undefined, buttonLabel: string, isWebviewAvailable: boolean, clickEvent: any) =>
  (event: React.MouseEvent<HTMLAnchorElement>) => {
    switch (targeting) {
      case 'Cookie Setting Osano':
        window?.Osano?.cm?.showDrawer?.('osano-cm-dom-info-dialog-open');
        event.preventDefault();
        break;
      case 'Cookie Setting':
        event.preventDefault();
        if (isWebviewAvailable) {
          clickEvent({ id: 'cookieSettings', text: buttonLabel });
        }
        break;
      case 'Soft Logged':
        logout('hard_logged');
        break;
      case 'Live Chat':
        event.preventDefault();
        dwChat.open();
        break;
      default:
        // No action needed for other targeting types
        break;
    }
  };

const renderIconImage = (iconImageField: LinkEntryType['fields']['icon']) => {
  if (!iconImageField) {
    return null;
  }

  if ((iconImageField as ImageEntryType)?.fields?.largeDeviceImage) {
    return <CmsImage contentItem={iconImageField as ImageEntryType} />;
  }

  const linkIcon = iconImageField as IconEntryType;
  const iconStyle = css`
    color: ${(linkIcon?.fields?.colour as unknown as ColourField)?.value};
  `;

  return (
    <div css={iconStyle}>
      <Icon kind={linkIcon.fields.iconType} size="m" />
    </div>
  );
};

interface AddToBasketContentProps {
  productCode: string;
  fullWidth: boolean | undefined;
  title: string | undefined;
  buttonStyle: ButtonVariant;
  hit: ProductProps | undefined;
  contentItem: LinkEntryType;
  cssButton: any;
  enableMobileStickyAddToBasket: boolean | undefined;
}

const renderAddToBasketContent = ({
  productCode,
  fullWidth,
  title,
  buttonStyle,
  hit,
  contentItem,
  cssButton,
  enableMobileStickyAddToBasket,
}: AddToBasketContentProps) => {
  const buttonComponent = () => (
    <Suspense fallback={<div>Loading...</div>}>
      <Button
        itemCode={productCode}
        qty={'1'}
        buttonText={title || ''}
        buttonStyle={buttonStyle}
        hit={hit}
        {...inspectorMode(contentItem?.sys?.id, 'title')}
      />
    </Suspense>
  );

  return (
    <>
      <div className={classNames('cart-button-wrap', { 'full-width': fullWidth })} css={cssButton}>
        {buttonComponent()}
      </div>
      {enableMobileStickyAddToBasket && (
        <div className={classNames('cart-button-wrap', 'stick-add-to-cart')} css={cssButton}>
          {buttonComponent()}
        </div>
      )}
    </>
  );
};

const renderPlainTextContent = (
  icon: LinkEntryType['fields']['icon'],
  buttonLabel: string,
  wrapper: any,
  variantType: any,
) => (
  <div className="plainTextWithIcon" css={css([wrapper, variantType])}>
    {icon && renderIconImage(icon)}
    {buttonLabel}
  </div>
);

const renderLinkContent = (
  cssStyles: any,
  categoryType: string,
  linkClass: string,
  handleClickEvent: (e: React.MouseEvent<HTMLAnchorElement>) => void,
  openInNewTab: boolean | undefined,
  contentItem: LinkEntryType,
  icon: LinkEntryType['fields']['icon'],
) => {
  const { title, entryTitle, url } = contentItem?.fields || {};
  const buttonLabel = title || entryTitle;

  return (
    <Link
      data-testid="widget-CmsLink"
      css={css(cssStyles)}
      href={url || ''}
      data-event={`${categoryType}Click`}
      data-section={`${categoryType}:${buttonLabel}`}
      data-action="navigation"
      data-name={`${categoryType} navigation`}
      data-text={buttonLabel}
      data-nav-label={buttonLabel}
      className={linkClass}
      onClick={handleClickEvent}
      target={openInNewTab ? '_blank' : '_self'}
      {...inspectorMode(contentItem?.sys?.id, 'title')}
    >
      {icon && renderIconImage(icon)}
      {buttonLabel}
    </Link>
  );
};

export const CmsLink = memo(({ contentItem, category, className, dlClickEventHandler, hit }: CmsLinkProps) => {
  const theme = useTheme();
  const pathname = usePathname();
  const categoryType = category || 'link';

  const { isWebviewAvailable, clickEvent } = useReactWebviewCommunicator();

  const {
    title,
    entryTitle,
    url,
    targeting,
    icon,
    variant,
    productCode,
    fullWidth,
    openInNewTab,
    enableMobileStickyAddToBasket,
  } = contentItem?.fields || {};

  const linkClass = useMemo(
    () =>
      classNames(className, {
        [UK_COOKIE_TARGET]: targeting === 'Cookie Setting' && !isWebviewAvailable,
        active: pathname === url,
      }),
    [className, isWebviewAvailable, pathname, targeting, url],
  );

  if (!contentItem?.fields) {
    return null;
  }
  if (!url) {
    logger.warn('No url value set for', contentItem.fields);
  }

  const buttonLabel = title || entryTitle;

  const cssButton = css`
    ${theme.widgets?.CmsLink?.button}
  `;
  const wrapper = css`
    display: block;
  `;

  const onClickHandler = createClickHandler(targeting, buttonLabel, isWebviewAvailable, clickEvent);
  const variantType = getVariantStyles(variant, theme);
  const buttonStyle = getButtonStyle(variant);

  const findSectionTitle = (element: HTMLElement): string | null => {
    const { parentElement } = element;
    const heading = parentElement?.querySelector('h2');
    return heading ? heading.innerText : null;
  };

  const handleClickEvent = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClickHandler(e);
    const sectionTitle = findSectionTitle(e.currentTarget);
    const clickText = (buttonLabel ?? '').trim();
    if (!clickText) {
      return;
    }
    const sectionOrEntryTitle = ((sectionTitle ?? entryTitle) ?? '').trim();
    dlClickEventHandler?.(`${clickText}:button:${sectionOrEntryTitle}:${url || ''}`);
  };

  if (targeting === 'Add to Basket' && productCode) {
    return renderAddToBasketContent({
      productCode,
      fullWidth,
      title,
      buttonStyle,
      hit,
      contentItem,
      cssButton,
      enableMobileStickyAddToBasket,
    });
  }

  if (targeting === 'Plain Text') {
    return renderPlainTextContent(icon, buttonLabel, wrapper, variantType);
  }

  return renderLinkContent(
    [wrapper, variantType],
    categoryType,
    linkClass,
    handleClickEvent,
    openInNewTab,
    contentItem,
    icon,
  );
});
